import { type ClassValue, clsx } from "clsx";
import { format, formatDistanceToNow } from "date-fns";
import DOMPurify from 'dompurify';
import { twMerge } from "tailwind-merge";
import { NewsItem } from "./types";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Sanitize HTML content
export const sanitizeHtml = (html: string) => DOMPurify.sanitize(html);

// Format a date for display
export function formatDate(date: string | Date | null): string {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return format(dateObj, 'HH:mm');
}

// Format a date relative to now
export function formatRelativeDate(date: string | Date | null): string {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return formatDistanceToNow(dateObj, { addSuffix: true });
}

// Get appropriate color class based on sentiment - using Bloomberg terminal-like colors
export function getSentimentColor(sentiment: string): string {
  switch (sentiment) {
    case 'positive':
      return 'text-[#4DAF4A]'; // Green - less bright, closer to Bloomberg green
    case 'negative':
      return 'text-[#E41A1C]'; // Red - closer to Bloomberg red
    case 'neutral':
    default:
      return 'text-[#F5F5F5]'; // Default white - neutral news in Bloomberg
  }
}

// Format news provider abbreviation
export function formatProvider(provider: string): string {
  return provider.toUpperCase();
}

// Truncate text to a specified length
export function truncateText(text: string, maxLength: number): string {
  if (!text || text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
}

// Sort news items by time
export function sortNewsByTime(news: NewsItem[]): NewsItem[] {
  return [...news].sort((a, b) => {
    return new Date(b.pubDate).getTime() - new Date(a.pubDate).getTime();
  });
}

// Sort news items by ID (for terminal-style display)
export function sortNewsById(news: NewsItem[]): NewsItem[] {
  return [...news].sort((a, b) => a.id - b.id);
}

// Extract providers from news items
export function extractProviders(news: NewsItem[]): string[] {
  console.log('Extracting providers from', news.length, 'news items');

  // Track unique providers and their counts for debugging
  const providerCounts: Record<string, number> = {};

  const providers = new Set<string>();
  news.forEach(item => {
    if (item.provider) {
      providers.add(item.provider);

      // Count for debugging
      providerCounts[item.provider] = (providerCounts[item.provider] || 0) + 1;
    }
  });

  // Log counts for debugging
  console.log('Provider counts:', providerCounts);

  // Return sorted array
  return Array.from(providers).sort();
}

// Sector → provider mapping
export const SECTOR_PROVIDERS: Record<string, string[]> = {
  'Finance & Markets': ['WSJ', 'FT', 'BBG', 'CNBC', 'MW', 'SP', 'BW', 'INVESTING', 'YHOO'],
  'Crypto & Digital Assets': ['COINDESK', 'COINTELEGRAPH', 'DECRYPT', 'THEBLOCK', 'CRYPTONEWS', 'NEWSBTC', 'BITCOINNEWS', 'ETHWORLDNEWS', 'ETHNEWS'],
  'Technology': ['TC', 'WIRD', 'VERGE'],
  'World News': ['BBC', 'RTRS', 'AJ', 'SKYNEWS', 'DW', 'NPR', 'GRDN', 'WAPO', 'HP', 'AXIO', 'NYT', 'UST', 'ADV'],
};

export function getSectors(): string[] {
  return Object.keys(SECTOR_PROVIDERS);
}

export function getProviderSector(provider: string): string {
  for (const [sector, providers] of Object.entries(SECTOR_PROVIDERS)) {
    if (providers.includes(provider.toUpperCase())) return sector;
  }
  return 'World News';
}

// Extract regions from news items
export function extractRegions(news: NewsItem[]): string[] {
  const regions = new Set<string>();

  // Add regions from news items
  news.forEach(item => {
    if (item.region && typeof item.region === 'string' && item.region.trim()) {
      regions.add(item.region.trim());
    }
  });

  // Return sorted array with Global first
  return Array.from(regions).sort((a, b) => {
    if (a === 'Global') return -1;
    if (b === 'Global') return 1;
    return a.localeCompare(b);
  });
}

// Get region display name with icons
export function getRegionDisplayName(region: string): string {
  const regionIcons: Record<string, string> = {
    'Global': '🌐',
    'North America': '🌎',
    'US': '🇺🇸',
    'UK': '🇬🇧',
    'Europe': '🇪🇺',
    'Asia': '🌏',
    'Middle East': '🕌',
    'Latin America': '🌴',
    'Africa': '🌍',
    'Oceania': '🏝️'
  };

  const icon = regionIcons[region] || '';
  return icon ? `${icon} ${region}` : region;
}

// Get color for region
export function getRegionColor(region: string): string {
  const regionColors: Record<string, string> = {
    'Global': 'text-slate-100',
    'North America': 'text-blue-300',
    'US': 'text-blue-400',
    'UK': 'text-indigo-300',
    'Europe': 'text-orange-400',
    'Asia': 'text-red-300',
    'Middle East': 'text-emerald-300',
    'Latin America': 'text-orange-300',
    'Africa': 'text-purple-300',
    'Oceania': 'text-teal-300'
  };

  return regionColors[region] || 'text-slate-100';
}
